import React, { useState, useEffect, useRef } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import './AdminLogin.css';

function AdminLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(null);
  const emailInputRef = useRef(null);

  // Auto-focus email input on mount
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // Check lockout status
  useEffect(() => {
    const storedLockout = localStorage.getItem('admin_lockout');
    if (storedLockout) {
      const lockoutEnd = parseInt(storedLockout, 10);
      const now = Date.now();
      if (now < lockoutEnd) {
        setIsLocked(true);
        setLockoutTime(lockoutEnd);
      } else {
        localStorage.removeItem('admin_lockout');
        localStorage.removeItem('admin_attempts');
      }
    }

    const storedAttempts = localStorage.getItem('admin_attempts');
    if (storedAttempts) {
      setAttempts(parseInt(storedAttempts, 10));
    }
  }, []);

  // Update lockout timer
  useEffect(() => {
    if (isLocked && lockoutTime) {
      const interval = setInterval(() => {
        const now = Date.now();
        if (now >= lockoutTime) {
          setIsLocked(false);
          setLockoutTime(null);
          setAttempts(0);
          localStorage.removeItem('admin_lockout');
          localStorage.removeItem('admin_attempts');
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isLocked, lockoutTime]);

  const getRemainingLockoutTime = () => {
    if (!lockoutTime) return 0;
    const remaining = Math.ceil((lockoutTime - Date.now()) / 1000);
    return Math.max(0, remaining);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if locked out
    if (isLocked) {
      const remaining = getRemainingLockoutTime();
      setError(`Too many failed attempts. Please try again in ${remaining} seconds.`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // Reset attempts on successful login
      localStorage.removeItem('admin_attempts');
      localStorage.removeItem('admin_lockout');
      setAttempts(0);
      onLoginSuccess();
    } catch (err) {
      // Security: Don't reveal specific error details
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      localStorage.setItem('admin_attempts', newAttempts.toString());

      // Lock after 5 failed attempts
      if (newAttempts >= 5) {
        const lockoutDuration = 15 * 60 * 1000; // 15 minutes
        const lockoutEnd = Date.now() + lockoutDuration;
        setIsLocked(true);
        setLockoutTime(lockoutEnd);
        localStorage.setItem('admin_lockout', lockoutEnd.toString());
        setError('Too many failed login attempts. Account temporarily locked for 15 minutes.');
      } else {
        // Generic error message
        setError('Invalid email or password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const remainingLockoutSeconds = getRemainingLockoutTime();
  const minutes = Math.floor(remainingLockoutSeconds / 60);
  const seconds = remainingLockoutSeconds % 60;

  return (
    <div className="admin-login-page">
      <div className="admin-login-background"></div>
      <div className="admin-login-container">
        <div className="admin-login-brand">
          <div className="admin-login-logo">THE TAG</div>
          <div className="admin-login-subtitle">Admin Portal</div>
        </div>

        <div className="admin-login-card">
          <div className="admin-login-header">
            <h1>Sign In</h1>
            <p>Enter your credentials to access the admin panel</p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login-form" noValidate>
            {error && (
              <div className={`admin-login-error ${isLocked ? 'error-locked' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 6v4M10 14h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            {isLocked && (
              <div className="admin-login-lockout">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="4" y="8" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
                  <path d="M7 8V5a3 3 0 0 1 6 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Account locked. Try again in {minutes}:{seconds.toString().padStart(2, '0')}</span>
              </div>
            )}

            <div className="admin-login-field">
              <label htmlFor="email">Email Address</label>
              <div className="admin-login-input-wrapper">
                <svg className="admin-login-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M2.5 6.66667L10 11.6667L17.5 6.66667M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  disabled={isLoading || isLocked}
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                />
              </div>
            </div>

            <div className="admin-login-field">
              <label htmlFor="password">Password</label>
              <div className="admin-login-input-wrapper">
                <svg className="admin-login-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="4" y="8" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M7 8V5a3 3 0 0 1 6 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isLoading || isLocked}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="admin-login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading || isLocked}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M10 3.75C6.25 3.75 3.10833 5.83333 1.66667 9.16667C3.10833 12.5 6.25 14.5833 10 14.5833C13.75 14.5833 16.8917 12.5 18.3333 9.16667C16.8917 5.83333 13.75 3.75 10 3.75Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M2.5 2.5L17.5 17.5M7.5 7.5C6.83333 8.16667 6.25 9.16667 6.25 10C6.25 11.3807 7.36929 12.5 8.75 12.5C9.58333 12.5 10.3333 12.0833 10.8333 11.5M12.5 9.16667C12.9167 9.58333 13.3333 10.0833 13.75 10.8333M3.33333 3.33333L5.83333 5.83333M16.6667 16.6667L14.1667 14.1667M10 3.75C6.25 3.75 3.10833 5.83333 1.66667 9.16667C3.10833 12.5 6.25 14.5833 10 14.5833C13.75 14.5833 16.8917 12.5 18.3333 9.16667C17.9167 8.33333 17.4167 7.66667 16.8333 7.08333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="admin-login-button"
              disabled={isLoading || isLocked}
            >
              {isLoading ? (
                <>
                  <span className="admin-login-spinner"></span>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="admin-login-security">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="3" y="7" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M5.5 7V4.5C5.5 3.11929 6.61929 2 8 2C9.38071 2 10.5 3.11929 10.5 4.5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>Secure authentication via Firebase</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;

